import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { getAllowedTabs, type AdminTab } from '../lib/rolePermissions';

export interface CurrentUser {
  id: string;
  role: string;
  isActive: boolean;
  allowedTabs: AdminTab[];
  permissions: Record<string, any>;
}

async function fetchUserData(uid: string): Promise<CurrentUser | null> {
  // Use the get-user-role edge function which bypasses RLS and handles first-user bootstrap
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;

  let role = 'viewer';
  let isActive = true;

  if (token) {
    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/get-user-role`;
      const res = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
      });
      if (res.ok) {
        const data = await res.json();
        role = (data.role ?? 'viewer').toLowerCase();
        isActive = data.is_active ?? true;
      }
    } catch {
      // Fall back to direct query if edge function unavailable
      const { data: authRow } = await supabase
        .from('auth_users')
        .select('role, is_active')
        .eq('id', uid)
        .maybeSingle();
      if (authRow) {
        role = (authRow.role ?? 'viewer').toLowerCase();
        isActive = authRow.is_active ?? true;
      }
    }
  }

  // Load custom roles for permission mapping
  const { data: customRoles } = await supabase
    .from('user_roles')
    .select('name, permissions');

  const customRole = (customRoles ?? []).find(
    r => r.name.toLowerCase() === role
  );
  const permissions: Record<string, any> = customRole?.permissions ?? {};
  let allowedTabs = getAllowedTabs(role, permissions);

  // Load messaging permissions from system_settings for built-in roles
  const { data: msgSetting } = await supabase
    .from('system_settings')
    .select('value')
    .eq('key', 'role_messaging_permissions')
    .maybeSingle();

  if (msgSetting?.value && typeof msgSetting.value === 'object') {
    const msgPerms = msgSetting.value as Record<string, boolean>;
    if (msgPerms[role] === false) {
      allowedTabs = allowedTabs.filter(t => t !== 'messaging');
    } else if (msgPerms[role] === true && !allowedTabs.includes('messaging')) {
      allowedTabs.push('messaging');
    }
  }

  return {
    id: uid,
    role,
    isActive,
    allowedTabs,
    permissions,
  };
}

export function useCurrentUser() {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let initialLoad = true;
    let lastUserId: string | null = null;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (initialLoad) return;

      const uid = session?.user?.id ?? null;
      if (uid === lastUserId) return;
      lastUserId = uid;

      if (session?.user) {
        setLoading(true);
        fetchUserData(session.user.id)
          .then(u => { setUser(u); })
          .catch(() => { setUser(null); })
          .finally(() => { setLoading(false); });
      } else {
        setUser(null);
        setLoading(false);
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      const uid = data.session?.user?.id ?? null;
      lastUserId = uid;

      if (data.session?.user) {
        fetchUserData(data.session.user.id)
          .then(u => { setUser(u); })
          .catch(() => { setUser(null); })
          .finally(() => { setLoading(false); initialLoad = false; });
      } else {
        setLoading(false);
        initialLoad = false;
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return { user, loading };
}
